<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { Check } from 'lucide-vue-next'
import type { CheckboxRootEmits, CheckboxRootProps } from 'reka-ui'
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

import { cn } from '../../../lib/utils'

export type CheckboxValue = boolean | 'on' | 'off' | 'indeterminate'

const properties = defineProps<
  Omit<CheckboxRootProps, 'modelValue'> & {
    class?: HTMLAttributes['class']
    disabled?: boolean
  }
>()
const emits = defineEmits<CheckboxRootEmits>()

// NOTE: When inputs are controlled by vee-validate field the value can be 'on' or 'off' but this is not supported by radix/reka.
// This is a workaround for that, ideally not a permanent solution but this might be the only way to solve it. (it breaks indeterminate option)
// Note that we still emit the value as boolean, this seems to work fine with vee-validate.
const modelValue = defineModel<CheckboxValue>({
  get: (value: CheckboxValue): boolean => (typeof value === 'boolean' ? value : value === 'on'),
  set: (value): CheckboxValue => (value === 'indeterminate' ? false : value),
})

const delegatedProperties = computed(() => {
  const { class: _, ...delegated } = properties

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProperties, emits)
</script>

<template>
  <CheckboxRoot :key="`checkbox-${modelValue}`" v-bind="forwarded" :class="cn('atabix-checkbox-root peer', properties.class)" :disabled="disabled">
    <CheckboxIndicator class="atabix-checkbox-indicator">
      <slot>
        <Check class="size-4" />
      </slot>
    </CheckboxIndicator>
  </CheckboxRoot>
</template>

<style lang="scss">
.atabix-checkbox-root {
  @apply h-4;
  @apply w-4;
  @apply shrink-0;
  @apply rounded-sm;
  @apply border;
  @apply border-button-ghostprimary-text;
  @apply ring-offset-background;
  @apply focus-visible:outline-none;
  @apply focus-visible:ring-2;
  @apply focus-visible:ring-border;
  @apply focus-visible:ring-offset-2;
  @apply disabled:cursor-not-allowed;
  @apply disabled:opacity-50;

  &[data-state='checked'] {
    @apply bg-button-ghostprimary-text;
  }

  &[data-state='checked'] {
    @apply text-foreground-light;
  }
}

.atabix-checkbox-indicator {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  @apply w-full;
  @apply h-full;
  @apply text-current;
}
</style>
